import React, { Component } from 'react';
import ReactDOM from 'react-dom';

import { requestJson } from '~/modules/legacyRequest.js';
import { personalProjectsEnabled, trackPersonalProjectEvent } from '~/modules/personalProjectAnalytics.js';

let nextProjectActionInstanceId = 0;
const activeProjectsPromises = new Map();
let activeProjectsCacheVersion = 0;

function invalidateActiveProjectsCache() {
  activeProjectsPromises.clear();
  activeProjectsCacheVersion += 1;
}

class ProjectAddTo extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      isCreateProjectOpen: false,
      status: 'closed',
      projects: [],
      newProjectTitle: '',
      message: '',
      errorKind: null,
      pendingProjectId: null,
      successProjectPath: null
    };

    this.actionId = props.actionId || `catalog-project-action-${props.trackId}-${props.trackVariantId || 'track'}-${nextProjectActionInstanceId += 1}`;
    this.pendingTrackVariantId = null;
    this.triggerRef = React.createRef();
    this.closeButtonRef = React.createRef();
    this.createProjectToggleRef = React.createRef();
    this.panelRef = React.createRef();
    this.portalRoot = null;
    this.bodyHadNoScroll = false;
    this.projectRequestVersion = 0;
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleRetryClick = this.handleRetryClick.bind(this);
    this.handleCreateProjectToggle = this.handleCreateProjectToggle.bind(this);
    this.handleProjectTitleChange = this.handleProjectTitleChange.bind(this);
    this.handleCreateProject = this.handleCreateProject.bind(this);
    this.positionPanel = this.positionPanel.bind(this);
  }

  componentDidMount() {
    if (!this.customerSignedIn() || typeof window === 'undefined' || !window.location) {
      return;
    }

    this.createPortalRoot();

    const searchParams = new URLSearchParams(window.location.search || '');
    const pendingTrackId = searchParams.get('catalog_project_action_track_id');
    const pendingVariantId = searchParams.get('catalog_project_action_track_variant_id');
    const configuredVariantAction = this.isVariantAction();
    const pendingVariantMatches = configuredVariantAction ? pendingVariantId === String(this.trackVariantId()) : true;
    if (pendingTrackId !== String(this.props.trackId) || !pendingVariantMatches) {
      this.loadInitialProjects();
      return;
    }

    if (!configuredVariantAction && pendingVariantId) {
      this.pendingTrackVariantId = pendingVariantId;
    }

    searchParams.delete('catalog_project_action_track_id');
    searchParams.delete('catalog_project_action_track_variant_id');
    const search = searchParams.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash || ''}`);
    this.handleTriggerClick({ preventDefault() {}, stopPropagation() {} });
  }

  initialProjectListPath() {
    const path = this.props.projectListPath || '/c/projects.json?status=active';
    const separator = path.indexOf('?') === -1 ? '?' : '&';
    const variantQuery = this.isVariantAction() ? `&track_id=${encodeURIComponent(this.props.trackId)}&track_variant_id=${encodeURIComponent(this.trackVariantId())}` : '';
    return `${path}${separator}include_track_assignments=true${variantQuery}`;
  }

  loadInitialProjects() {
    if (!this.customerSignedIn()) {
      return Promise.resolve();
    }

    const requestPath = this.initialProjectListPath();
    const cacheVersion = activeProjectsCacheVersion;
    if (!activeProjectsPromises.has(requestPath)) {
      activeProjectsPromises.set(requestPath, requestJson(requestPath));
    }

    const requestVersion = this.projectRequestVersion;
    const projectsPromise = activeProjectsPromises.get(requestPath);
    return projectsPromise.then((data) => {
      if (requestVersion !== this.projectRequestVersion || cacheVersion !== activeProjectsCacheVersion || this.state.isOpen) {
        return;
      }

      const projects = Array.isArray(data) ? data : (data.projects || []);
      const normalizedProjects = projects.map((project) => {
        const projectTrackId = project.project_track_ids?.[String(this.props.trackId)];
        const normalizedProject = {
          ...project,
          contains_track: this.isVariantAction() ? project.contains_track : Boolean(projectTrackId),
          project_track_id: project.project_track_id || projectTrackId || null,
        };
        if (this.isVariantAction()) {
          normalizedProject.contains_variant = project.contains_variant === true;
          normalizedProject.project_track_variant_id = project.project_track_variant_id || null;
        }
        return normalizedProject;
      });
      this.setState({
        projects: normalizedProjects,
        isCreateProjectOpen: normalizedProjects.length === 0
      });
    }).catch(() => {}).finally(() => {
      if (activeProjectsPromises.get(requestPath) === projectsPromise) {
        activeProjectsPromises.delete(requestPath);
      }
    });
  }

  componentDidUpdate(previousProps, previousState) {
    const previousCreateProjectOpen = previousState.projects.length === 0 || previousState.isCreateProjectOpen;
    const createProjectOpen = this.state.projects.length === 0 || this.state.isCreateProjectOpen;

    const projectLoadingFinished = previousState.status === 'loading' && this.state.status === 'ready';
    if (previousCreateProjectOpen && !createProjectOpen && !projectLoadingFinished) {
      this.createProjectToggleRef.current?.focus();
    }

    if (!previousState.isOpen && this.state.isOpen) {
      this.closeButtonRef.current?.focus();
      this.lockBodyScroll();
      this.addPositionListeners();
      this.positionPanel();
    }

    if (previousState.isOpen && !this.state.isOpen) {
      this.unlockBodyScroll();
      this.removePositionListeners();
      this.resetPanelPosition();
      this.triggerRef.current?.focus();
    }

    if (this.state.isOpen) {
      this.positionPanel();
    }
  }

  componentWillUnmount() {
    this.projectRequestVersion += 1;
    this.unlockBodyScroll();
    this.removePositionListeners();
    this.portalRoot?.remove();
    this.portalRoot = null;
  }

  lockBodyScroll() {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }

    this.bodyHadNoScroll = document.body.classList.contains('noscroll');
    document.body.classList.add('noscroll');
  }

  unlockBodyScroll() {
    if (typeof document === 'undefined' || !document.body || this.bodyHadNoScroll) {
      return;
    }

    document.body.classList.remove('noscroll');
    this.bodyHadNoScroll = false;
  }

  createPortalRoot() {
    if (this.portalRoot || typeof document === 'undefined' || !document.body) {
      return;
    }

    this.portalRoot = document.createElement('div');
    this.portalRoot.className = this.props.portalClassName || 'catalog-project-action__portal';
    document.body.appendChild(this.portalRoot);
  }

  addPositionListeners() {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('resize', this.positionPanel);
    window.addEventListener('scroll', this.positionPanel, true);
  }

  removePositionListeners() {
    if (typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('resize', this.positionPanel);
    window.removeEventListener('scroll', this.positionPanel, true);
  }

  resetPanelPosition() {
    const panel = this.panelRef.current;
    if (!panel) {
      return;
    }

    panel.style.left = '';
    panel.style.top = '';
    panel.style.right = '';
    panel.style.bottom = '';
    panel.style.transform = '';
  }

  positionPanel() {
    if (!this.state.isOpen || typeof window === 'undefined') {
      return;
    }

    const panel = this.panelRef.current;
    const trigger = this.triggerRef.current;
    if (!panel || !trigger) {
      return;
    }

    const isMobile = window.innerWidth <= 767;
    if (isMobile) {
      this.resetPanelPosition();
      return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportMargin = 16;
    const triggerGap = 12;
    const maxLeft = Math.max(viewportMargin, viewportWidth - panelRect.width - viewportMargin);
    const maxTop = Math.max(viewportMargin, viewportHeight - panelRect.height - viewportMargin);
    let left = triggerRect.left - panelRect.width - triggerGap;
    if (left < viewportMargin) {
      left = triggerRect.right + triggerGap;
    }

    const top = Math.min(maxTop, Math.max(viewportMargin, triggerRect.top + (triggerRect.height - panelRect.height) / 2));
    left = Math.min(maxLeft, Math.max(viewportMargin, left));
    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
    panel.style.right = 'auto';
    panel.style.bottom = 'auto';
    panel.style.transform = 'none';
  }

  customerSignedIn() {
    if (typeof this.props.customerSignedIn === 'boolean') {
      return this.props.customerSignedIn;
    }

    return (document.body?.getAttribute('data-user-tracking-context') || '').indexOf('customer') === 0;
  }

  isVariantAction() {
    const trackVariantId = this.trackVariantId();
    return trackVariantId !== null && typeof trackVariantId !== 'undefined' && trackVariantId !== '';
  }

  trackVariantId() {
    return this.pendingTrackVariantId || this.props.trackVariantId;
  }

  trackVariantTitle() {
    if (this.props.trackVariantTitle) {
      return this.props.trackVariantTitle;
    }

    const variant = (this.props.trackVariants || []).find((entry) => String(entry.id) === String(this.trackVariantId()));
    return variant?.title;
  }

  itemLabel() {
    if (this.isVariantAction()) {
      return this.props.locale === 'de' ? 'Variante' : 'variant';
    }

    return this.props.locale === 'de' ? 'Track' : 'track';
  }

  projectEntrySelected(project) {
    return this.isVariantAction() ? project.contains_variant === true : project.contains_track === true;
  }

  projectListPath() {
    const path = this.props.projectListPath || '/c/projects.json?status=active';
    const separator = path.indexOf('?') === -1 ? '?' : '&';
    const variantQuery = this.isVariantAction() ? `&track_variant_id=${encodeURIComponent(this.trackVariantId())}` : '';
    return `${path}${separator}track_id=${encodeURIComponent(this.props.trackId)}${variantQuery}`;
  }

  projectTrackPath(projectId) {
    return `/c/projects/${projectId}/project_tracks.json`;
  }

  projectPath(project) {
    return project?.project_path || `/c/projects/${project?.id}`;
  }

  guestSignupPath() {
    const location = typeof window === 'undefined' ? null : window.location;
    const returnTo = `${location?.pathname || '/'}${location?.search || ''}${location?.hash || ''}`;

    const variantQuery = this.isVariantAction() ? `&track_variant_id=${encodeURIComponent(this.trackVariantId())}` : '';
    return `/c/projects/project_tracks/auth_gate?track_id=${encodeURIComponent(this.props.trackId)}${variantQuery}&return_to=${encodeURIComponent(returnTo)}`;
  }

  handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!personalProjectsEnabled()) {
      return;
    }

    if (this.state.isOpen) {
      this.handleCloseClick(event);
      return;
    }

    this.props.onOpenChange?.(true);
    trackPersonalProjectEvent('personal_project_add_modal_opened', {
      track_id: this.props.trackId,
      track_variant_id: this.trackVariantId(),
      surface: this.props.analyticsSurface || (this.isVariantAction() ? 'catalog_variant' : 'catalog_track')
    });
    this.setState({ isOpen: true, status: 'loading', message: '', errorKind: null });
    return this.loadProjects();
  }

  loadProjects() {
    const requestVersion = this.projectRequestVersion += 1;

    return requestJson(this.projectListPath())
      .then((data) => {
        if (requestVersion !== this.projectRequestVersion) {
          return;
        }

        const projects = Array.isArray(data) ? data : (data.projects || []);
        this.setState({
          status: 'ready',
          projects,
          isCreateProjectOpen: projects.length === 0,
          errorKind: null
        });
      })
      .catch(() => {
        if (requestVersion !== this.projectRequestVersion) {
          return;
        }

        this.setState({
          status: 'error',
          message: this.loadErrorText(),
          errorKind: 'load'
        });
      });
  }

  handleCloseClick(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    this.projectRequestVersion += 1;
    this.props.onOpenChange?.(false);
    this.setState({ isOpen: false, isCreateProjectOpen: false, status: 'closed', message: '', errorKind: null, pendingProjectId: null, successProjectPath: null });
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.handleCloseClick(event);
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusableElements = Array.from(event.currentTarget.querySelectorAll('a[href], button:not([disabled]), input:not([disabled])')).filter((element) => !element.closest('[hidden]'));
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  handleRetryClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.setState({ status: 'loading', message: '', errorKind: null });
    return this.loadProjects();
  }

  handleCreateProjectToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    this.setState((state) => ({ isCreateProjectOpen: !state.isCreateProjectOpen }));
  }

  handleProjectTitleChange(event) {
    this.setState({ newProjectTitle: event.target.value });
  }

  handleProjectToggle(project) {
    if (this.projectEntrySelected(project)) {
      return this.removeTrackFromProject(project);
    }

    return this.addTrackToProject(project);
  }

  handleCreateProject(event) {
    event.preventDefault();
    event.stopPropagation();

    const title = this.state.newProjectTitle.trim();
    if (!title) {
      this.setState({ status: 'ready', isCreateProjectOpen: true, message: this.props.locale === 'de' ? 'Bitte geben Sie einen Projektnamen ein.' : 'Please enter a project name.', errorKind: 'create' });
      return;
    }

    const requestVersion = this.projectRequestVersion += 1;
    this.setState({ status: 'saving', message: '' });
    return requestJson('/c/projects.json', {
      method: 'POST',
      data: { 'project[title]': title }
    })
      .then((data) => {
        if (requestVersion !== this.projectRequestVersion) {
          return null;
        }

        return this.addTrackToProject(data.project, title, requestVersion, true);
      })
      .catch(() => {
        if (requestVersion !== this.projectRequestVersion) {
          return;
        }

        this.setState({ status: 'error', isCreateProjectOpen: true, message: this.createErrorText(), errorKind: 'create' });
      });
  }

  addTrackToProject(project, fallbackTitle, requestVersion = this.projectRequestVersion += 1, collapseCreateProject = false) {
    if (!project || !project.id) {
      this.setState({ status: 'error', message: this.addErrorText(), errorKind: 'add' });
      return Promise.resolve();
    }

    this.setState({ status: 'saving', message: '', pendingProjectId: project.id, successProjectPath: null });
    const requestData = { track_id: this.props.trackId };
    if (this.isVariantAction()) {
      requestData.track_variant_id = this.trackVariantId();
    }

    return requestJson(this.projectTrackPath(project.id), {
      method: 'POST',
      data: requestData
    })
      .then((data) => {
        if (requestVersion !== this.projectRequestVersion) {
          return;
        }

        invalidateActiveProjectsCache();
        const projectTitle = project.title || fallbackTitle;
        const alreadyPresent = data && data.already_present;
        const selectedKey = this.isVariantAction() ? 'contains_variant' : 'contains_track';
        const assignmentKey = this.isVariantAction() ? 'project_track_variant_id' : 'project_track_id';
        const assignmentId = this.isVariantAction() ? data.project_track_variant?.id : data.project_track?.id;
        const projectTrackId = data.project_track?.id || project.project_track_id;
        const projects = this.state.projects.some((entry) => entry.id === project.id) ?
          this.state.projects.map((entry) => entry.id === project.id ? { ...entry, [selectedKey]: true, [assignmentKey]: assignmentId || entry[assignmentKey], project_track_id: projectTrackId || entry.project_track_id } : entry) :
          [{ ...project, [selectedKey]: true, [assignmentKey]: assignmentId, project_track_id: projectTrackId }].concat(this.state.projects);

        this.setState({
          status: 'success',
          projects: projects,
          newProjectTitle: '',
          message: alreadyPresent ? this.duplicateText(projectTitle) : this.successText(projectTitle),
          errorKind: null,
          pendingProjectId: null,
          successProjectPath: this.projectPath(project),
          isCreateProjectOpen: collapseCreateProject ? false : this.state.isCreateProjectOpen
        });
      })
      .catch(() => {
        if (requestVersion !== this.projectRequestVersion) {
          return;
        }

        this.setState({ status: 'error', message: this.addErrorText(), errorKind: 'add', pendingProjectId: null });
      });
  }

  removeTrackFromProject(project) {
    const assignmentId = this.isVariantAction() ? project?.project_track_variant_id : project?.project_track_id;
    if (!project || !assignmentId) {
      this.setState({ status: 'error', message: this.removeErrorText(), errorKind: 'remove' });
      return Promise.resolve();
    }

    const requestVersion = this.projectRequestVersion += 1;
    this.setState({ status: 'saving', message: '', pendingProjectId: project.id, successProjectPath: null });
    const removePath = this.isVariantAction() ?
      `/c/projects/${project.id}/project_tracks/${project.project_track_id}/project_track_variants/${assignmentId}.json` :
      `/c/projects/${project.id}/project_tracks/${assignmentId}.json`;
    return requestJson(removePath, { method: 'DELETE' })
      .then(() => {
        if (requestVersion !== this.projectRequestVersion) {
          return;
        }

        invalidateActiveProjectsCache();
        this.setState({
          status: 'success',
          projects: this.state.projects.map((entry) => entry.id === project.id ? (this.isVariantAction() ? { ...entry, contains_variant: false, project_track_variant_id: null } : { ...entry, contains_track: false, project_track_id: null }) : entry),
          message: this.removeSuccessText(project.title),
          errorKind: null,
          pendingProjectId: null
        });
      })
      .catch(() => {
        if (requestVersion !== this.projectRequestVersion) {
          return;
        }

        this.setState({ status: 'error', message: this.removeErrorText(), errorKind: 'remove', pendingProjectId: null });
      });
  }

  successText(projectTitle) {
    const itemTitle = this.trackVariantTitle() || this.props.trackTitle;
    if (itemTitle) {
      if (this.isVariantAction()) {
        return this.props.locale === 'de' ? `Variante „${itemTitle}“ wurde zu „${projectTitle}“ hinzugefügt.` : `Variant “${itemTitle}” was added to “${projectTitle}”.`;
      }

      return this.props.locale === 'de' ? `„${itemTitle}“ wurde zu „${projectTitle}“ hinzugefügt.` : `“${itemTitle}” was added to “${projectTitle}”.`;
    }

    if (!this.isVariantAction()) {
      return this.props.locale === 'de' ? `Zu „${projectTitle}“ hinzugefügt.` : `Added to “${projectTitle}”.`;
    }

    return this.props.locale === 'de' ? `Variante zu „${projectTitle}“ hinzugefügt.` : `Added the variant to “${projectTitle}”.`;
  }

  trackLabel() {
    const title = this.trackVariantTitle() || this.props.trackTitle;
    return title ? ` „${title}“` : '';
  }

  duplicateText(projectTitle) {
    return this.props.locale === 'de' ? `Bereits in „${projectTitle}“ gespeichert.` : `Already saved in “${projectTitle}”.`;
  }

  loadErrorText() {
    return this.props.locale === 'de' ? 'Die Projekte konnten nicht geladen werden. Bitte versuchen Sie es erneut.' : 'The projects could not be loaded. Please try again.';
  }

  createErrorText() {
    return this.props.locale === 'de' ? 'Das Projekt konnte nicht angelegt werden. Bitte versuchen Sie es erneut.' : 'The project could not be created. Please try again.';
  }

  addErrorText() {
    return this.props.locale === 'de' ? `Die ${this.itemLabel()} konnte nicht zum Projekt hinzugefügt werden.` : `The ${this.itemLabel()} could not be added to the project.`;
  }

  removeSuccessText(projectTitle) {
    return this.props.locale === 'de' ? `Aus „${projectTitle}“ entfernt.` : `Removed from “${projectTitle}”.`;
  }

  removeErrorText() {
    return this.props.locale === 'de' ? `Die ${this.itemLabel()} konnte nicht aus dem Projekt entfernt werden.` : `The ${this.itemLabel()} could not be removed from the project.`;
  }

  hasAssignedProject() {
    return this.state.projects.some((project) => this.projectEntrySelected(project));
  }

  assignedProjectCount() {
    return this.state.projects.filter((project) => this.projectEntrySelected(project)).length;
  }

  triggerLabel() {
    return this.props.locale === 'de' ? 'Projekt' : 'Project';
  }

  triggerAriaLabel() {
    const itemLabel = this.itemLabel();
    if (this.hasAssignedProject()) {
      const count = this.assignedProjectCount();
      return this.props.locale === 'de' ? `${itemLabel}${this.trackLabel()} in ${count} ${count === 1 ? 'Projekt' : 'Projekten'} gespeichert` : `${itemLabel}${this.trackLabel()} saved in ${count} ${count === 1 ? 'project' : 'projects'}`;
    }

    return this.props.locale === 'de' ? `${itemLabel}${this.trackLabel()} zu Projekt hinzufügen` : `Add ${itemLabel}${this.trackLabel()} to a project`;
  }

  renderProjectPanel() {
    if (!this.state.isOpen) {
      return null;
    }

    const isGerman = this.props.locale === 'de';
    const itemTitle = this.trackVariantTitle() || this.props.trackTitle;
    const isBusy = this.state.status === 'loading' || this.state.status === 'saving';
    const panelId = `${this.actionId}-panel`;
    const panelTitleId = `${this.actionId}-panel-title`;
    const panelDescriptionId = `${this.actionId}-panel-description`;
    const titleInputId = `${this.actionId}-title`;
    const createErrorId = `${this.actionId}-create-error`;
    const createProjectSectionId = `${this.actionId}-create-project`;
    const isLoadError = this.state.errorKind === 'load';
    const isCreateError = this.state.errorKind === 'create';
    const assignedProjectCount = this.assignedProjectCount();
    const projectCount = this.state.projects.length;
    const projectCountLabel = projectCount === 1 ? (isGerman ? 'Projekt' : 'project') : (isGerman ? 'Projekten' : 'projects');
    const createProjectOpen = projectCount === 0 || this.state.isCreateProjectOpen;
    const createProjectToggleLabel = createProjectOpen ? (isGerman ? 'Neues Projekt ausblenden' : 'Hide new project form') : (isGerman ? 'Neues Projekt anlegen' : 'Create new project');

    const panelMarkup = (
      <>
        <div className='catalog-project-action__backdrop' aria-hidden='true' onClick={this.handleCloseClick}></div>
        <div ref={this.panelRef} id={panelId} className='catalog-project-action__panel' role='dialog' aria-modal='true' aria-busy={isBusy} aria-labelledby={`${panelTitleId} ${panelDescriptionId}`} tabIndex='-1' onKeyDown={this.handleKeyDown} onClick={(event) => event.stopPropagation()}>
          <div className='catalog-project-action__panel-header'>
            <div>
              <h2 id={panelTitleId}>{isGerman ? 'Zu Projekt hinzufügen' : 'Add to project'}</h2>
              <p id={panelDescriptionId} className='catalog-project-action__track-context'>{itemTitle ? `„${itemTitle}“` : (isGerman ? (this.isVariantAction() ? 'Diese Variante speichern' : 'Diesen Track speichern') : `Save this ${this.itemLabel()}`)}</p>
            </div>
            <button ref={this.closeButtonRef} type='button' className='catalog-project-action__close' onClick={this.handleCloseClick} aria-label={isGerman ? 'Schließen' : 'Close'}>
              <i className='fa fa-times'></i>
            </button>
          </div>

          {projectCount > 0 ? <p className='catalog-project-action__summary'><strong>{isGerman ? `Fügen Sie ${this.isVariantAction() ? 'diese Variante' : 'diesen Track'} zu Ihren Projekten hinzu.` : `Add this ${this.itemLabel()} to your projects.`}</strong><span>{isGerman ? `${assignedProjectCount} von ${projectCount} ${projectCountLabel} ausgewählt. Sie können mehrere Projekte auswählen.` : `${assignedProjectCount} of ${projectCount} ${projectCountLabel} selected. You can select multiple projects.`}</span></p> : null}

          {this.state.status === 'loading' ? <p className='catalog-project-action__status' role='status' aria-live='polite'>{isGerman ? 'Persönliche Projekte werden geladen …' : 'Loading personal projects …'}</p> : null}

          {isLoadError ? (
            <div className='catalog-project-action__load-error'>
              <p className='catalog-project-action__status' role='alert' aria-live='assertive'>{this.state.message}</p>
              <button type='button' className='catalog-project-action__retry' onClick={this.handleRetryClick}>{isGerman ? 'Erneut laden' : 'Try again'}</button>
            </div>
          ) : null}

          {this.state.status !== 'loading' && !isLoadError ? (
            <div className='catalog-project-action__project-list'>
              {this.state.projects.length > 0 ? this.state.projects.map((project) => {
                const containsTrack = this.projectEntrySelected(project);

                const projectPath = this.projectPath(project);
                const projectCheckboxId = `${this.actionId}-project-${project.id}`;
                const toggleLabel = isGerman ? (containsTrack ? `${this.itemLabel()}${this.trackLabel()} aus „${project.title}“ entfernen` : `${this.itemLabel()}${this.trackLabel()} zu „${project.title}“ hinzufügen`) : (containsTrack ? `Remove ${this.itemLabel()}${this.trackLabel()} from “${project.title}”` : `Add ${this.itemLabel()}${this.trackLabel()} to “${project.title}”`);

                return (
                  <div className={`catalog-project-action__project${containsTrack ? ' catalog-project-action__project--selected' : ''}`} key={project.id}>
                    <label className='catalog-project-action__project-checkbox-control' htmlFor={projectCheckboxId}>
                      <input id={projectCheckboxId} type='checkbox' className='catalog-project-action__project-checkbox' checked={containsTrack} onChange={() => this.handleProjectToggle(project)} disabled={isBusy} aria-label={toggleLabel} />
                      <span className='catalog-project-action__project-name'>{project.title}</span>
                    </label>
                    <a className='catalog-project-action__project-link' href={projectPath} aria-label={isGerman ? `Zum Projekt: „${project.title}“` : `Open project: “${project.title}”`}>{isGerman ? 'Zum Projekt' : 'Open project'}</a>
                  </div>
                );
              }) : <p className='catalog-project-action__status'>{isGerman ? 'Noch kein aktives persönliches Projekt vorhanden.' : 'No active personal project yet.'}</p>}
            </div>
          ) : null}

          {projectCount > 0 && !isLoadError && this.state.status !== 'loading' ? (
            <button ref={this.createProjectToggleRef} type='button' className='catalog-project-action__create-toggle' onClick={this.handleCreateProjectToggle} aria-expanded={createProjectOpen} aria-controls={createProjectSectionId}>
              <i className={createProjectOpen ? 'fa fa-minus' : 'fa fa-plus'} aria-hidden='true'></i>
              <span>{createProjectToggleLabel}</span>
            </button>
          ) : null}

          {this.state.status !== 'loading' && !isLoadError ? (
            <form id={createProjectSectionId} className='catalog-project-action__create' hidden={projectCount > 0 && !createProjectOpen} onSubmit={this.handleCreateProject}>
              <label htmlFor={titleInputId}>
                {isGerman ? 'Neues Projekt' : 'New project'}
              </label>
              <div className='catalog-project-action__create-row'>
                <input id={`${titleInputId}`} type='text' value={this.state.newProjectTitle} onChange={this.handleProjectTitleChange} maxLength='120' placeholder={isGerman ? 'Projektname' : 'Project name'} disabled={isBusy} required aria-invalid={isCreateError && !this.state.newProjectTitle.trim()} aria-describedby={isCreateError ? createErrorId : undefined} />
                <button type='submit' className='catalog-project-action__create-button' disabled={isBusy} aria-label={isGerman ? 'Projekt anlegen' : 'Create project'}>
                  <i className='fa fa-plus' aria-hidden='true'></i>
                  <span>{isGerman ? 'Anlegen' : 'Create'}</span>
                </button>
              </div>
            </form>
          ) : null}

          {this.state.message && !isLoadError ? <p id={isCreateError ? createErrorId : undefined} className={`catalog-project-action__message catalog-project-action__message--${this.state.status}`} role={this.state.status === 'error' ? 'alert' : 'status'} aria-live='polite'>{this.state.message}</p> : null}
          {this.state.status === 'success' && this.state.successProjectPath ? <a className='catalog-project-action__success-link' href={this.state.successProjectPath}>{isGerman ? 'Projekt öffnen' : 'Open project'}</a> : null}
          {this.state.status === 'saving' ? <p className='catalog-project-action__status' role='status' aria-live='polite'>{isGerman ? 'Projekt wird aktualisiert …' : 'Updating project …'}</p> : null}
        </div>
      </>
    );

    return this.portalRoot ? ReactDOM.createPortal(panelMarkup, this.portalRoot) : panelMarkup;
  }

  render() {
    if (this.props.enabled !== true || !personalProjectsEnabled()) {
      return null;
    }

    const isSuccess = this.hasAssignedProject();
    const isGerman = this.props.locale === 'de';

    if (!this.customerSignedIn()) {
      return (
        <div className='catalog-project-action'>
          <a className='catalog-project-action__trigger catalog-project-action__trigger--guest' href={this.guestSignupPath()} aria-label={this.triggerAriaLabel()} title={isGerman ? 'Zu Projekt hinzufügen' : 'Add to project'}>
            <i className='fa fa-folder-open'></i>
            <span>{isGerman ? 'Projekt' : 'Project'}</span>
          </a>
        </div>
      );
    }

    return (
      <div className='catalog-project-action'>
        <button ref={this.triggerRef} type='button' className={`catalog-project-action__trigger${isSuccess ? ' catalog-project-action__trigger--success' : ''}`} onClick={this.handleTriggerClick} aria-expanded={this.state.isOpen} aria-haspopup='dialog' aria-controls={`${this.actionId}-panel`} aria-label={this.triggerAriaLabel()}>
          <i className='fa fa-folder-open'></i>
          <span>{this.triggerLabel()}</span>
          {this.assignedProjectCount() > 0 ? <span className='catalog-project-action__trigger-count' aria-hidden='true'>{this.assignedProjectCount()}</span> : null}
        </button>
        {this.renderProjectPanel()}
      </div>
    );
  }
}

export default ProjectAddTo;
